/**
 * "Form Foundry" — original showcase level (difficulty 3/10) that walks
 * through every player form plus pads and orbs. Authored in block coordinates;
 * produces standard SSL LevelData. Sections:
 * Runner → Glider corridor → Roller flips → Hopper fences → Streak tunnel →
 * Vaulter walls → Blinker alternations → Pendul arcs → pad/orb finale.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function spike(bx: number, by = 0, hanging = false): void {
  objects.push({
    id: nextId++,
    type: 'spike',
    ...cell(bx, by),
    ...(hanging ? { flipY: true } : {}),
  });
}

function block(bx: number, by = 0): void {
  objects.push({ id: nextId++, type: 'block', ...cell(bx, by) });
}

function column(bx: number, fromY: number, toY: number): void {
  for (let by = fromY; by <= toY; by++) block(bx, by);
}

function ceiling(bx0: number, bx1: number, by: number): void {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
}

function portal(bx: number, kind: string, by = 1): void {
  objects.push({ id: nextId++, type: 'portal', ...cell(bx, by), props: { kind } });
}

function pad(bx: number, kind: string, by = 0): void {
  objects.push({ id: nextId++, type: 'pad', ...cell(bx, by), props: { kind } });
}

function orb(bx: number, by: number, kind: string): void {
  objects.push({ id: nextId++, type: 'orb', ...cell(bx, by), props: { kind } });
}

// --- Runner intro (0–60) -----------------------------------------------------
spike(25);
spike(35);
spike(45);
portal(58, 'form:glider');

// --- Glider corridor (62–115) ------------------------------------------------
ceiling(62, 112, 6);
column(72, 0, 1); // floor pillar
column(80, 4, 5); // stalactite
column(88, 0, 1);
column(96, 4, 5);
column(104, 0, 1);
portal(114, 'form:roller');

// --- Roller flips (116–160) ---------------------------------------------------
ceiling(116, 156, 5);
spike(124);
spike(125); // ground → be on ceiling
spike(136, 4, true);
spike(137, 4, true); // ceiling → be on ground
spike(144);
spike(145); // ground → ceiling
spike(152, 4, true);
spike(153, 4, true); // ceiling → end grounded
portal(162, 'form:hopper');

// --- Hopper fences (162–205) --------------------------------------------------
spike(172);
spike(173);
spike(182);
spike(183);
spike(192);
spike(193);
portal(208, 'form:streak');

// --- Streak tunnel (210–255) --------------------------------------------------
ceiling(210, 253, 6);
column(220, 0, 1); // stalagmite
column(230, 4, 5); // stalactite
column(240, 0, 1);
portal(258, 'form:vaulter');

// --- Vaulter walls (258–300) --------------------------------------------------
column(268, 0, 1);
column(280, 0, 1);
column(292, 0, 1);
portal(304, 'form:blinker');

// --- Blinker alternations (306–345) --------------------------------------------
ceiling(306, 343, 5);
spike(314);
spike(315); // ground → blink up
spike(320, 4, true);
spike(321, 4, true); // ceiling → blink down
spike(330);
spike(331); // ground → blink up
spike(336, 4, true);
spike(337, 4, true); // ceiling → blink down (ends grounded)
portal(348, 'form:pendul');

// --- Pendul arcs (348–390) -----------------------------------------------------
spike(358);
spike(359);
spike(368);
spike(369);
spike(378);
spike(379);
// Catch the pendul at either altitude; both portals restore the finale state.
portal(392, 'form:runner', 1);
portal(392, 'form:runner', 4);
portal(393, 'gravity:down', 1);
portal(393, 'gravity:down', 4);

// --- Pad & orb finale (396–425) -------------------------------------------------
pad(400, 'strong');
spike(401);
spike(402);
spike(403);
orb(412, 1, 'strong');
spike(412);
spike(413);
spike(414);

// Secret shards spread across the form sections.
objects.push({ id: nextId++, type: 'collectible', ...cell(30, 2), props: { kind: 'shard' } });
objects.push({ id: nextId++, type: 'collectible', ...cell(176, 2), props: { kind: 'shard' } });
objects.push({ id: nextId++, type: 'collectible', ...cell(388, 2), props: { kind: 'shard' } });

objects.push({ id: nextId++, type: 'endgate', ...cell(424, 2) });

export const FORM_FOUNDRY: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Form Foundry',
    author: 'Shape Shifters Team',
    description: 'Every form, forged in neon.',
    revision: 1,
    createdAt: '2026-07-23T00:00:00.000Z',
    updatedAt: '2026-07-23T00:00:00.000Z',
    difficulty: 3,
    tags: ['showcase', 'official'],
  },
  song: { id: 'circuit-breaker', bpm: 140, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#a78bfa',
    hazardColor: '#fb7185',
    backgroundColor: '#140626',
  },
  objects,
};
